import { useEffect, useMemo, useState } from 'react';
import {
    Folder, FileText, Youtube, FolderPlus, Upload, Trash2, Home, ArrowRight, ArrowLeft,
    Edit3, Download as DLIcon, ExternalLink, Search, GripVertical,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { openFileNode } from '../../utils/fileDownload';
import { FILE_NODE_SORT_OPTIONS, sortFileNodes } from '../../utils/fileNodeSort';

/** Admin Downloads — manage the FileNode tree (folders / files / YouTube).
 *  Same nav as sales + create / upload / **rename** / **download** / delete. */
export default function AdminDownloads() {
    const [tree, setTree] = useState([]);
    const [parent, setParent] = useState(null);
    const [crumbs, setCrumbs] = useState([]);
    const [q, setQ] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);

    const load = () => api.get('/files/tree').then(r => {
        setTree(r.data.items || []);
        if (r.data.sortBy) setSortBy(r.data.sortBy);
    });
    useEffect(() => { load(); }, []);

    const childrenOf = (parentId) => tree.filter(n => String(n.parent || '') === String(parentId || ''));

    const visible = useMemo(() => {
        let items;
        if (q.trim()) {
            const term = q.toLowerCase();
            items = tree.filter(n => n.name.toLowerCase().includes(term));
        } else {
            items = childrenOf(parent);
        }
        return sortFileNodes(items, sortBy);
    }, [tree, parent, q, sortBy]);

    const enter = (f) => { setParent(f._id); setCrumbs(c => [...c, f]); setQ(''); };
    const goTo  = (i) => { const n = crumbs.slice(0, i + 1); setCrumbs(n); setParent(n.length ? n[n.length-1]._id : null); };
    const goHome = () => { setCrumbs([]); setParent(null); };
    const goUp = () => goTo(crumbs.length - 2);

    const newFolder = async () => {
        const name = prompt('Folder name:');
        if (!name) return;
        await api.post('/files/folder', { name, parent });
        toast.success('Folder created');
        load();
    };
    const addYoutube = async () => {
        const youtubeUrl = prompt('Paste YouTube URL:');
        if (!youtubeUrl) return;
        const name = prompt('Display name:', 'Video') || 'Video';
        try { await api.post('/files/youtube', { youtubeUrl, name, parent }); toast.success('Added'); load(); } catch {}
    };
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        if (parent) fd.append('parent', parent);
        fd.append('name', file.name);
        try { await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Uploaded'); load(); } catch {}
    };

    /** Rename ANY node (folder, file or youtube) — backend PATCH /api/files/:id */
    const rename = async (n) => {
        const next = prompt(`Rename "${n.name}" to:`, n.name);
        if (!next || next.trim() === n.name) return;
        try {
            await api.patch(`/files/${n._id}`, { name: next.trim() });
            toast.success('Renamed');
            load();
        } catch {/* toasted */}
    };

    /** Download a file (streams via API so production serves the real PDF, not the SPA). */
    const downloadFile = async (n) => {
        try {
            await openFileNode(n);
        } catch { /* toasted by api interceptor */ }
    };

    const previewFile = async (n) => {
        try {
            await openFileNode(n, { preview: true });
        } catch { /* toasted */ }
    };

    const remove = async (n) => {
        if (!confirm(`Delete "${n.name}"?${n.type === 'folder' ? ' All contents will be deleted.' : ''}`)) return;
        await api.delete(`/files/${n._id}`);
        toast.success('Deleted');
        load();
    };

    /* ── Drag-and-drop reordering (admin only, current folder, not while searching) ── */
    const canReorder = !q;

    const persistOrder = async (orderedIds) => {
        const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
        // Optimistic: stamp new order on the affected nodes and flip to manual sort
        setTree(prev => prev.map(n => orderMap.has(n._id) ? { ...n, order: orderMap.get(n._id) } : n));
        const wasManual = sortBy === 'manual';
        if (!wasManual) setSortBy('manual');
        try {
            await api.patch('/files/reorder', { ids: orderedIds });
            if (!wasManual) await api.patch('/files/sort-preference', { sortBy: 'manual' });
            toast.success('Order saved for all users');
        } catch {
            toast.error('Could not save order');
            load();
        }
    };

    const onDragStart = (e, id) => {
        if (!canReorder) return;
        setDragId(id);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', id); } catch { /* some browsers */ }
    };
    const onDragOver = (e, id) => {
        if (!canReorder || !dragId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (id !== dragOverId) setDragOverId(id);
    };
    const onDrop = (e, targetId) => {
        if (!canReorder) return;
        e.preventDefault();
        const sourceId = dragId || (() => { try { return e.dataTransfer.getData('text/plain'); } catch { return null; } })();
        setDragId(null);
        setDragOverId(null);
        if (!sourceId || sourceId === targetId) return;
        const ordered = visible.map(n => n._id);
        const from = ordered.indexOf(sourceId);
        const to = ordered.indexOf(targetId);
        if (from < 0 || to < 0) return;
        ordered.splice(to, 0, ordered.splice(from, 1)[0]);
        persistOrder(ordered);
    };
    const onDragEnd = () => { setDragId(null); setDragOverId(null); };

    const onSortChange = async (e) => {
        const next = e.target.value;
        const prev = sortBy;
        setSortBy(next);
        try {
            await api.patch('/files/sort-preference', { sortBy: next });
            toast.success('Sort order applied for all users');
        } catch {
            setSortBy(prev);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">Downloads — admin</h1>
                    <p className="text-sm text-ink-500">Manage the file tree. Folders, files and YouTube videos all supported. Sort order applies to all users. Click <kbd className="bg-ink-100 px-1 rounded text-[10px]">edit</kbd> to rename, or <span className="font-semibold text-ink-600">drag the grip</span> to rearrange.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button className="ag-btn-ghost" onClick={newFolder}><FolderPlus className="h-4 w-4" /> New folder</button>
                    <label className="ag-btn-primary cursor-pointer">
                        <Upload className="h-4 w-4" /> Upload file
                        <input type="file" className="hidden" onChange={onUpload} />
                    </label>
                    <button className="ag-btn-ghost" onClick={addYoutube}><Youtube className="h-4 w-4" /> Add YouTube</button>
                </div>
            </div>

            {/* Search + sort */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative flex-1 min-w-0">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                    <input className="ag-input pl-9 w-full" placeholder="Search files or folders…" value={q} onChange={e => setQ(e.target.value)} />
                </div>
                <label className="flex items-center gap-2 shrink-0 sm:ml-auto">
                    <p className="text-sm font-semibold text-ink-500">Sort by:</p>
                    <span className="text-xs font-semibold text-ink-500 sm:sr-only">Sort</span>
                    <select
                        className="ag-input w-full sm:w-auto sm:min-w-[190px] text-sm"
                        value={sortBy}
                        onChange={onSortChange}
                        aria-label="Sort folders and files for all users"
                    >
                        {FILE_NODE_SORT_OPTIONS.map(o => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                    </select>
                </label>
            </div>

            {/* Breadcrumb */}
            {!q && (
            <div className="flex items-center gap-1.5 text-sm">
                {crumbs.length > 0 && <button className="ag-btn-ghost py-1 px-2" onClick={goUp}><ArrowLeft className="h-4 w-4" /></button>}
                <button className="flex items-center gap-1 ag-link" onClick={goHome}><Home className="h-4 w-4" /> Home</button>
                {crumbs.map((c, i) => (
                    <span key={c._id} className="flex items-center gap-1.5">
                        <ArrowRight className="h-3.5 w-3.5 text-ink-300" />
                        <button className="ag-link truncate max-w-[160px]" onClick={() => goTo(i)}>{c.name}</button>
                    </span>
                ))}
            </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {visible.map(n => (
                    <div key={n._id}
                         draggable={canReorder}
                         onDragStart={(e) => onDragStart(e, n._id)}
                         onDragOver={(e) => onDragOver(e, n._id)}
                         onDrop={(e) => onDrop(e, n._id)}
                         onDragEnd={onDragEnd}
                         className={`ag-card p-4 flex flex-col group transition
                            ${dragId === n._id ? 'opacity-40' : ''}
                            ${dragOverId === n._id && dragId !== n._id ? 'ring-2 ring-brand-teal-600 ring-offset-1' : ''}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-1.5">
                                {canReorder && (
                                    <span
                                        className="cursor-grab active:cursor-grabbing text-ink-300 hover:text-ink-600 -ml-1"
                                        title="Drag to reorder"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <GripVertical className="h-5 w-5" />
                                    </span>
                                )}
                                {n.type === 'folder'  && <Folder   className="h-8 w-8 text-brand-yellow" />}
                                {n.type === 'file'    && <FileText className="h-8 w-8 text-brand-red" />}
                                {n.type === 'youtube' && <Youtube  className="h-8 w-8 text-brand-red" />}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                <button className="text-ink-500 hover:text-brand-teal-700" onClick={() => rename(n)} title="Rename">
                                    <Edit3 className="h-4 w-4" />
                                </button>
                                <button className="text-ink-300 hover:text-brand-red" onClick={() => remove(n)} title="Delete">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <div onClick={() => n.type === 'folder' && enter(n)}
                             className={`mt-2 text-sm font-semibold line-clamp-2 ${n.type === 'folder' ? 'cursor-pointer hover:text-brand-teal-700' : ''}`}>
                            {n.name}
                        </div>

                        <div className="text-[11px] text-ink-500 mt-1">
                            {n.type === 'folder'  && '— folder —'}
                            {n.type === 'file'    && (n.fileSize ? `${(n.fileSize / 1024).toFixed(0)} KB · ${n.downloadCount || 0} dl` : `${n.downloadCount || 0} downloads`)}
                            {n.type === 'youtube' && 'YouTube'}
                        </div>

                        {/* Action bar — what was missing for admins */}
                        <div className="mt-3 flex gap-2">
                            {n.type === 'file' && (
                                <>
                                    <button className="ag-btn-primary text-xs flex-1 justify-center" onClick={() => downloadFile(n)}>
                                        <DLIcon className="h-3.5 w-3.5" /> Download
                                    </button>
                                    <button className="ag-btn-ghost text-xs" onClick={() => previewFile(n)} title="Preview">
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </button>
                                </>
                            )}
                            {n.type === 'youtube' && (
                                <a className="ag-btn-primary text-xs flex-1 justify-center" href={n.youtubeUrl} target="_blank" rel="noreferrer">
                                    <Youtube className="h-3.5 w-3.5" /> Watch
                                </a>
                            )}
                            {n.type === 'folder' && (
                                <button className="ag-btn-ghost text-xs flex-1 justify-center" onClick={() => enter(n)}>
                                    Open <ArrowRight className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {visible.length === 0 && (
                    <div className="col-span-full text-center text-sm text-ink-500 py-12">
                        {q ? `No results for "${q}"` : 'Empty folder. Add a folder, file or YouTube video above.'}
                    </div>
                )}
            </div>
        </div>
    );
}
